import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { MRT_RowSelectionState } from 'mantine-react-table';
import { Stack, Text } from '@mantine/core';
import { useSelectedSubTrades } from '../selected-sub-trades-context';
import TradeActions from '../trade-detail-actions';
import { MasterTradeParams } from 'app/models/master-trade-params';
import MainTable from 'app/views/components/Table/MainTable';
import { GridPaginationStorage, GridColumnVisibilityStorage, useGridPagination, useGridColumnVisibility } from 'app/utils/grid-sorting-storage';
import { SubTradeParams } from 'app/models/sub-trade-params';
import { useDispatch, useSelector } from 'react-redux';
import { getSubTrades } from 'app/state/ducks/sub-trades/selectors';
import { getFacilityAmounts, getLockedFacilities } from 'app/state/ducks/facilities/selectors';
import { getBusinessDate } from 'app/state/ducks/business-date/selectors';
import { getTradedFacilities } from 'app/state/ducks/traded-facilities/selectors';
import { RefinedSubTrade } from 'app/models/traded-facilities-params';
import { NewSelectInput } from 'app/views/components/inputs/selects/NewSelectInput';
import { getColumnDefs, SubTradeDocumentTableParams, DocumentType } from './document-trade-entry-panel-columnDefs';
import { docIconStatusView } from './doc-icon-status';
import { formatDocumentTimestamp } from './timestamp-format';
import DocumentTradePanel from './document-trade-panel';
import { ContractType, DocumentStateTypes } from 'app/models/common-types';
import { getDocuments } from 'app/state/ducks/documents/selectors';
import { Document, SignableDocument } from 'app/models/document-params'
import { Hold } from 'app/models/hold-params'
import DocumentPreviewModal from 'app/views/pages/trade-entry/detail-trade/documents/document-preview-modal';
import LiftHoldModal from 'app/views/components/holds/lift-hold-modal';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import {
  getSignableDocuments,
  getLockedFMMetadatas,
} from 'app/state/ducks/signable-document/selectors'
import { documentsRegenerated } from 'app/state/ducks/signable-document/actions'
import { getSubTradeSignableDocument, getMergedDocument, refreshDocument, liftHoldOnSubTrades } from 'app/state/ducks/signable-document/thunks'
import { ReactComponent as AllSignatoriesComplete } from 'app/public/DrawerIcons/all-signatures-complete.svg'
import { ReactComponent as AllLendosSignatoriesComplete } from 'app/public/DrawerIcons/all-lendos-signatures-complete.svg'
import { ReactComponent as AttachmentAdded } from 'app/public/DrawerIcons/attachment-added-to-trade.svg'
import { ReactComponent as SignatoriesActionPending } from 'app/public/DrawerIcons/signature-action-pending.svg'
import { getPossibleActions, onSignDocument } from '../../utils/signable-document-utils';
import { getSigningState, hasPartySigned } from '../../utils/signing-state';
import { SigningProgressIcon } from './signing-progress-icon';
import { useNavigate, useParams } from 'react-router-dom';
import { useUnappliedTransactions } from 'app/views/pages/trade-entry/utils/useUnappliedTransactions';
import { buildNotRefreshedDetailsBySubTradeId } from 'app/views/pages/trade-entry/utils/aa-document-not-refreshed';

import { getRefinedSubTrades } from '../../utils/master-trade-util';
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors';
import { getTradeDetailAaMetadata, getTradeDetailFullySignedAa } from 'app/state/ducks/trade-detail/selectors';
import { AAMetadataResponse, AADocumentMetaData } from 'app/models/aa-metadata-params';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import { getDeliveries } from 'app/state/ducks/trade-document-delivery/selectors';
import { deliveryViewFor, mostCurrentDelivery } from './delivery-view';
import { useTradePageDeals, useTradePageEntities, useTradePageFacilities, useTradePageWireInstructions } from 'app/views/pages/trade-entry/detail-trade/use-trade-page-lookups';
import { useDeliveryConfirm } from '../delivery-confirm-context';
import { useTradeRefresh } from 'app/views/pages/trade-entry/detail-trade/use-trade-refresh';


interface DetailPanelProps {
  row: MasterTradeParams;
}

const tradeViewColumns = ['All', 'Settled', 'Open'];

const getTextStyle = (color: string, weight = 600, textAlign?: 'start' | 'end' | 'center'): React.CSSProperties => ({
  fontFamily: 'Plus Jakarta Sans',
  fontSize: '14px',
  fontWeight: weight,
  lineHeight: '19px',
  textAlign: textAlign ?? 'center',
  color,
  width: '100%',
});

// A document is shown as signed (green icon) once both trade counterparties
// (Buyer + Seller) have signed — online (`signatures`) or offline via admin
// (`adminSignedFor`). This is the LOS-5960 rule and is used only for the
// C / FM columns (the Affiliate Letter has its own auto-signed path, and the
// AA column derives its progress states via getSigningState instead).
// We require explicit Buyer/Seller evidence rather than a zero-step shortcut so
// an empty/incomplete workflow can't flip the icon green.
const isSignableDocumentSigned = (doc?: SignableDocument): boolean =>
  !!doc && hasPartySigned(doc, 'Buyer') && hasPartySigned(doc, 'Seller')


const DocumentTradeEntryPanel: React.FC<DetailPanelProps> = ({ row }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Trade-level lock lift from the documents card. Facility/deal locks are
  // read-only here (lifted at their source), so only the Trade-origin hold
  // gets a Lift affordance — see the Alert column's makeLift below.
  const [liftTarget, setLiftTarget] = useState<{ subTradeId: string; hold: Hold } | null>(null);
  const { tradeId } = useParams<{ tradeId: string }>();

  const deliveries = useSelector(getDeliveries)
  const { openConfirm } = useDeliveryConfirm()
  const { refreshDocuments } = useTradeRefresh()

  const subTrades: SubTradeParams[] = useSelector(getSubTrades);
  const allEntities = useTradePageEntities();
  const activeFacilities = useTradePageFacilities();
  const lockedFacilities = useSelector(getLockedFacilities);
  const allFacilities = useMemo(
    () => [...activeFacilities, ...lockedFacilities],
    [activeFacilities, lockedFacilities],
  );
  const allTradedFacilities = useSelector(getTradedFacilities);
  const wireInstructions = useTradePageWireInstructions()
  const businessDate = useSelector(getBusinessDate);
  const allDeals = useTradePageDeals();
  const facilityAmounts = useSelector(getFacilityAmounts);
  const [statusTable, setStatusTable] = useState<string>('All');
  const allAttachments = useSelector(getDocuments)
  const allSignableDocuments = useSelector(getSignableDocuments)
  const lockedFMMetadatas = useSelector(getLockedFMMetadatas)
  const [attachementsMasterTrade, setAttachementsMasterTrade] = useState<Document[]>([])
  const [openPreviewDocument, setOpenPreviewDocument] = useState<boolean>(false);
  const [signableDocument, setSignableDocument] = useState<SignableDocument | null>(null);
  const [activeRefinedSubTrade, setActiveRefinedSubTrade] = useState<RefinedSubTrade | null>(null);
  const [currentDocumentType, setCurrentDocumentType] = useState<string>('');
  const holidayCalendars = useSelector(getCalendars)
  const [refinedSubTrades, setRefinedSubTrades] = useState<RefinedSubTrade[]>([]);
  const [isLoadingRefinedSubTrades, setIsLoadingRefinedSubTrades] = useState(false);
  // Both AA sets come with the page's per-trade read (ADR_LOS-030 step 3),
  // re-read after a regenerate: the fully signed set drives the agent-signed
  // settlement-date override; a document goes stale whether or not anyone has
  // signed it, so the not-refreshed check reads every AA.
  const fullySignedAa = useSelector(getTradeDetailFullySignedAa);
  const aaMetadata: AAMetadataResponse = useMemo(() => ({ success: true, data: fullySignedAa }), [fullySignedAa]);
  const isLoadingAAMetadata = false;
  const allAaMetadata: AADocumentMetaData[] = useSelector(getTradeDetailAaMetadata);
  const [refreshingDocument, setRefreshingDocument] = useState<{ subTradeId: string; documentType: DocumentType } | null>(null);
  const [refreshWarningModal, setRefreshWarningModal] = useState<{ subTradeId: string; documentType: DocumentType } | null>(null);
  const activeSessionRef = useRef<string | null>(null);

  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const { setSelectedSubTradeIds } = useSelectedSubTrades();
  useEffect(() => {
    setSelectedSubTradeIds(Object.keys(rowSelection).filter((id) => rowSelection[id]));
  }, [rowSelection, setSelectedSubTradeIds]);
  // Clear shared selection on unmount so it doesn't outlive the Documents tab.
  useEffect(() => {
    return () => setSelectedSubTradeIds([]);
  }, [setSelectedSubTradeIds]);

  const hasUnappliedTransactions = useUnappliedTransactions(row?.id);

  const redirectToSubTrade = () => {
    if (tradeId) {
      navigate(`/tradeentry/${tradeId}/subtrade`);
    }
  };

  const handleRefreshDocument = async (subTradeId: string, documentType: DocumentType, hasSignatures: boolean) => {
    // Check if document has existing signatures
    if (hasSignatures) {
      setRefreshWarningModal({ subTradeId, documentType });
      return;
    }
    // No signatures, proceed with refresh
    await executeRefreshDocument(subTradeId, documentType);
  };

  const executeRefreshDocument = async (subTradeId: string, documentType: DocumentType) => {
    setRefreshingDocument({ subTradeId, documentType });
    try {
      const response = await refreshDocument(subTradeId, documentType);
      if (!response.success) {
        ErrorNotification({
          title: 'Error Refreshing Document',
          message: response.payload || 'Failed to refresh document',
        });
        return;
      }
      // Re-read this trade's documents to update the Redux store (a fresh
      // read, ADR_LOS-030 step 3).
      await refreshDocuments();
      // Tell the AA Document Metadata fetches their copy is stale — the
      // regenerate just rewrote it, so the "not refreshed" flags should clear.
      dispatch(documentsRegenerated());
      SuccessNotification({
        title: 'Document Regenerated',
        message: 'The document has been successfully regenerated.',
      });
    } catch (error) {
      ErrorNotification({
        title: 'Error Refreshing Document',
        message: (error as Error).message,
      });
    } finally {
      setRefreshingDocument(null);
    }
  };

  const handleConfirmRefresh = async () => {
    if (refreshWarningModal) {
      setRefreshWarningModal(null);
      await executeRefreshDocument(refreshWarningModal.subTradeId, refreshWarningModal.documentType);
    }
  };

  const handleCancelRefresh = () => {
    setRefreshWarningModal(null);
  };

  const { pagination, handlePaginationChange } = useGridPagination(GridPaginationStorage.documentTradeEntry);
  const { columnVisibility, handleColumnVisibilityChange } = useGridColumnVisibility(GridColumnVisibilityStorage.documentTradeEntry);

  useEffect(() => {
    const documentStateType = DocumentStateTypes[ContractType.MasterTrade]
    setAttachementsMasterTrade(allAttachments[documentStateType] ?? [])
  }, [allAttachments])

  // Locked funding-memo metadata comes with the tab's documents read (the
  // page shell, ADR_LOS-030 step 3) — not a tenant-wide load.

  useEffect(() => {
    const loadRefinedSubTrades = async () => {
      if (!row?.id || !subTrades?.length) {
        setRefinedSubTrades([])
        setIsLoadingRefinedSubTrades(false)
        return
      }

      setIsLoadingRefinedSubTrades(true)
      try {
        const refined = getRefinedSubTrades(
          row,
          subTrades,
          allDeals,
          allEntities,
          allTradedFacilities,
          allFacilities,
          businessDate,
          holidayCalendars,
          wireInstructions,
          aaMetadata,
          isLoadingAAMetadata
        )
        setRefinedSubTrades(refined)

      } catch (error) {
        console.error('Error loading refined sub-trades:', error)
        setRefinedSubTrades([])
      } finally {
        setIsLoadingRefinedSubTrades(false)
      }
    }

    loadRefinedSubTrades()
  }, [
    row?.id,
    subTrades,
    allDeals?.length,
    allEntities?.length,
    allTradedFacilities?.length,
    allFacilities?.length,
    businessDate?.getTime(),
    holidayCalendars?.length,
    wireInstructions?.length,
    facilityAmounts?.length,
    aaMetadata?.data?.length,
    isLoadingAAMetadata
  ])

  // Deliveries come with the tab's documents read (ADR_LOS-030 step 3).

  const handleSignDocument = async (signBy: "buyer" | "seller" | "both") => {
    const session = activeSessionRef.current;
    const signed = await onSignDocument(signBy, signableDocument, dispatch);
    if (activeSessionRef.current !== session) return;
    if (signed) setSignableDocument(signed);
  };


  const handleOpenDocument = async (tradeId: string, documentType: string) => {
    const session = `${tradeId}:${documentType}`;
    activeSessionRef.current = session;
    const matchingSubTrade = refinedSubTrades.find(trade => trade.id === tradeId);
    if (matchingSubTrade && matchingSubTrade.id) {
      setActiveRefinedSubTrade(matchingSubTrade as RefinedSubTrade);
    }

    const isAA = documentType === 'AssignmentAgreementDocument';
    const response: any = isAA
      ? await getMergedDocument(tradeId, documentType)
      : await getSubTradeSignableDocument(tradeId, documentType);

    if (activeSessionRef.current !== session) return;
    if (!response || !response.success) {
      ErrorNotification({
        title: 'Error',
        message: response?.payload || `Failed to retrieve the ${isAA ? 'merged ' : ''}document.`,
      })
      return;
    }

    setSignableDocument(response.payload);
    setCurrentDocumentType(documentType);
    setOpenPreviewDocument(true);
  }
  const openPreview = async (refinedSubTrade: RefinedSubTrade, documentType: string) => {
    setActiveRefinedSubTrade(refinedSubTrade);
    await handleOpenDocument(refinedSubTrade.id ?? '', documentType);
  };


  // Compute refined sub-trades for this master trade.
  // Sub-trades whose AA document records an amount the trade has already moved
  // past — a refresh that did not happen. Only the AA can be checked this way:
  // C, FM and AL keep no amount snapshot to compare a document against.
  const notRefreshedBySubTradeId = useMemo(() => {
    const documents = allAaMetadata
    if (documents.length === 0 || refinedSubTrades.length === 0) return new Map()
    return buildNotRefreshedDetailsBySubTradeId(
      refinedSubTrades.map(st => ({
        id: st.id,
        masterTradeId: st.masterTradeId,
      })),
      allTradedFacilities,
      documents,
      allFacilities,
      row ? [row] : [],
    )
  }, [allAaMetadata, refinedSubTrades, allTradedFacilities, allFacilities, row])

  const subTradesForDocumentTable: SubTradeDocumentTableParams[] = useMemo(() => {
    // A sub-trade can carry one delivery per packet type (ADR_LOS-026), but
    // the Delivery cell shows the single most-current packet (LOS-6471) —
    // the record most recently acted on.
    const deliveriesById = new Map<string, typeof deliveries>()
    deliveries.forEach(d => {
      const list = deliveriesById.get(d.subTradeId) ?? []
      list.push(d)
      deliveriesById.set(d.subTradeId, list)
    })
    const deliveryViewFor2 = (subTradeId: string) => {
      const current = mostCurrentDelivery(deliveriesById.get(subTradeId) ?? [])
      if (!current) return null
      return { ...deliveryViewFor(current), packetType: current.packetType ?? ('settled' as const) }
    }
    return refinedSubTrades
      .map(trade => {
        const agentEntity = allEntities.find(e => e.id === trade.tradedFacilities[0]?.facilityDetails?.adminEntity.id)
        const documents = attachementsMasterTrade.filter((doc) => doc.contractId.id === trade.id);
        const signableDocuments = (allSignableDocuments ?? []).filter(doc => doc.contractId === trade.id);
        const documentTypeC = documents.filter(d => d.tags.includes('C'));
        const documentTypeAA = documents.filter(d => d.tags.includes('AA'));
        const documentTypeFM = documents.filter(d => d.tags.includes('FM'));
        const documentTypeAL = documents.filter(d => d.tags.includes('AL'));

        const signableDocumentTypeC = signableDocuments.find(doc => doc.documentType === "ConfirmationDocument");
        const signableDocumentTypeFM = signableDocuments.find(doc => doc.documentType === "FundingMemoDocument");
        const signableDocumentTypeAA = signableDocuments.find(doc => doc.documentType === "AssignmentAgreementDocument");
        const signableDocumentTypeAL = signableDocuments.find(doc => doc.documentType === "AffiliateLetterDocument");
        let cDocumentIcon: any = <SignatoriesActionPending onClick={() => handleOpenDocument(trade.id ?? '', "ConfirmationDocument")} style={{ width: '40px', cursor: "pointer" }} />
        // C has a single counterparty step (no Agent), so its ladder is
        // pending > amber S / B (one counterparty signed) > green (both).
        // Green stays on the evidence-based LOS-5960 rule; only the partial
        // states come from getSigningState.
        const cSigningState = getSigningState(signableDocumentTypeC);
        if (!signableDocumentTypeC) {
          cDocumentIcon = null;
        }
        else if (isSignableDocumentSigned(signableDocumentTypeC)) {
          // Signed: paperclip icon when an offline signed document is attached,
          // otherwise the "signed & reviewed at signing portal" icon.
          cDocumentIcon = (signableDocumentTypeC.adminSignedFor?.length ?? 0) > 0
            ? <AllSignatoriesComplete onClick={() => handleOpenDocument(trade.id ?? '', "ConfirmationDocument")} style={{ width: '40px', cursor: "pointer" }} />
            : <AllLendosSignatoriesComplete onClick={() => handleOpenDocument(trade.id ?? '', "ConfirmationDocument")} style={{ width: '40px', cursor: "pointer" }} />
        }
        else if (cSigningState === 'seller' || cSigningState === 'buyer') {
          cDocumentIcon = <SigningProgressIcon variant={cSigningState} isOffline={(signableDocumentTypeC.adminSignedFor?.length ?? 0) > 0} onClick={() => handleOpenDocument(trade.id ?? '', "ConfirmationDocument")} style={{ width: '40px', cursor: "pointer" }} />
        }
        else if (documentTypeC && documentTypeC.length > 0) {
          cDocumentIcon = <AttachmentAdded onClick={() => handleOpenDocument(trade.id ?? '', "ConfirmationDocument")} style={{ width: '40px', cursor: "pointer" }} />
        }
        let aaDocumentIcon: any = <SignatoriesActionPending onClick={() => handleOpenDocument(trade.id ?? '', "AssignmentAgreementDocument")} style={{ width: '40px', cursor: "pointer" }} />

        let aaDocumentSigned = false;
        // AA signing progress: executed (green, unchanged) > both counterparties
        // signed awaiting Agent/Reviewer (blue check) > one counterparty signed
        // (amber S / B) > attachment / pending (unchanged). Online + offline
        // signatures both count — see getSigningState.
        const aaSigningState = getSigningState(signableDocumentTypeAA);
        if (!signableDocumentTypeAA) {
          aaDocumentIcon = null;
        }
        // AA goes green only when FULLY EXECUTED — i.e. no required signature
        // steps remain — not merely when both counterparties have signed. This
        // also gates settlement eligibility (columnDefs `isSettleEligible`).
        else if (aaSigningState === 'executed') {
          // Signed: paperclip icon when an offline signed document is attached,
          // otherwise the "signed & reviewed at signing portal" icon.
          aaDocumentIcon = (signableDocumentTypeAA.adminSignedFor?.length ?? 0) > 0
            ? <AllSignatoriesComplete onClick={() => handleOpenDocument(trade.id ?? '', "AssignmentAgreementDocument")} style={{ width: '40px', cursor: "pointer" }} />
            : <AllLendosSignatoriesComplete onClick={() => handleOpenDocument(trade.id ?? '', "AssignmentAgreementDocument")} style={{ width: '40px', cursor: "pointer" }} />
          aaDocumentSigned = true
        }
        // Any in-progress state ('executed' was handled above) renders the
        // progress icon — written as !== 'none' so a future SigningState
        // member is included here automatically instead of silently falling
        // through to the attachment/pending icons.
        else if (aaSigningState !== 'none') {
          // isOffline mirrors the executed branch: any offline (admin-uploaded)
          // signature adds the paperclip composition.
          aaDocumentIcon = <SigningProgressIcon variant={aaSigningState} isOffline={(signableDocumentTypeAA.adminSignedFor?.length ?? 0) > 0} onClick={() => handleOpenDocument(trade.id ?? '', "AssignmentAgreementDocument")} style={{ width: '40px', cursor: "pointer" }} />
        }
        else if (documentTypeAA && documentTypeAA.length > 0) {
          aaDocumentIcon = <AttachmentAdded onClick={() => handleOpenDocument(trade.id ?? '', "AssignmentAgreementDocument")} style={{ width: '40px', cursor: "pointer" }} />
        }
        let fmDocumentIcon: any = <SignatoriesActionPending onClick={() => openPreview(trade as any, "FundingMemoDocument")} style={{ width: '40px', cursor: "pointer" }} data-test-id="fm-document-generate-button" />
        const fmSigningState = getSigningState(signableDocumentTypeFM);

        if (!signableDocumentTypeFM) {
          fmDocumentIcon = null;
        }
        else if (isSignableDocumentSigned(signableDocumentTypeFM)) {
          // Signed: paperclip icon when an offline signed document is attached,
          // otherwise the "signed & reviewed at signing portal" icon.
          fmDocumentIcon = (signableDocumentTypeFM.adminSignedFor?.length ?? 0) > 0
            ? <AllSignatoriesComplete onClick={() => openPreview(trade as any, "FundingMemoDocument")} style={{ width: '40px', cursor: "pointer" }} data-test-id="fm-document-generate-button" />
            : <AllLendosSignatoriesComplete onClick={() => openPreview(trade as any, "FundingMemoDocument")} style={{ width: '40px', cursor: "pointer" }} data-test-id="fm-document-generate-button" />
        }
        // FM mirrors C: single counterparty step, amber S / B for a partial
        // signature, green (LOS-5960 evidence rule) once both have signed.
        else if (fmSigningState === 'seller' || fmSigningState === 'buyer') {
          fmDocumentIcon = <SigningProgressIcon variant={fmSigningState} isOffline={(signableDocumentTypeFM.adminSignedFor?.length ?? 0) > 0} onClick={() => openPreview(trade as any, "FundingMemoDocument")} style={{ width: '40px', cursor: "pointer" }} dataTestId="fm-document-generate-button" />
        }
        else if (documentTypeFM && documentTypeFM.length > 0) {
          fmDocumentIcon = <AttachmentAdded onClick={() => openPreview(trade as any, "FundingMemoDocument")} style={{ width: '40px', cursor: "pointer" }} data-test-id="fm-document-generate-button" />
        }

        let alDocumentIcon: any = <SignatoriesActionPending onClick={() => handleOpenDocument(trade.id ?? '', "AffiliateLetterDocument")} style={{ width: '40px', cursor: "pointer" }} />

        if (!signableDocumentTypeAL) {
          alDocumentIcon = (
            <Text style={getTextStyle('#476DB9')}>
              N/A
            </Text>
          );
        }
        else {
          const autoSignedByBuyer = signableDocumentTypeAL.signatures?.some((sig: any) =>
            sig.signerType === "Buyer" && sig.userId === "auto-signing-service"
          );

          if (autoSignedByBuyer) {
            alDocumentIcon = <AllLendosSignatoriesComplete onClick={() => handleOpenDocument(trade.id ?? '', "AffiliateLetterDocument")} style={{ width: '40px', cursor: "pointer" }} />
          }
          else if (signableDocumentTypeAL?.requiredSignatures.steps.length === 0 && documentTypeAL && documentTypeAL.length > 0) {
            alDocumentIcon = <AllSignatoriesComplete onClick={() => handleOpenDocument(trade.id ?? '', "AffiliateLetterDocument")} style={{ width: '40px', cursor: "pointer" }} />
          }
          else if (documentTypeAL && documentTypeAL.length > 0) {
            alDocumentIcon = <AttachmentAdded onClick={() => handleOpenDocument(trade.id ?? '', "AffiliateLetterDocument")} style={{ width: '40px', cursor: "pointer" }} />
          }
        }

        // Status line under each document icon. Derived from the signable
        // document only — the icons keep their own (unchanged) rules above.
        // agencyParties lets the AA spot the offline-Agent fallback, where the
        // Reviewer/Agent signature steps point back at the agency's own parties
        // because the deal's admin entity has no company set up.
        const agencyParties = [trade.agencyAdmin, subTrades.find(st => st.id === trade.id)?.agencyManager]
        const cStatus = docIconStatusView(signableDocumentTypeC)
        const aaStatus = docIconStatusView(signableDocumentTypeAA, { isAssignmentAgreement: true, agencyParties })
        const fmStatus = docIconStatusView(signableDocumentTypeFM)
        const alStatus = docIconStatusView(signableDocumentTypeAL)

        // Check if each document type has signatures
        const cHasSignatures = signableDocumentTypeC?.signatures && signableDocumentTypeC.signatures.length > 0;
        const aaHasSignatures = signableDocumentTypeAA?.signatures && signableDocumentTypeAA.signatures.length > 0;
        const alHasSignatures = signableDocumentTypeAL?.signatures && signableDocumentTypeAL.signatures.length > 0;
        // FM counts offline (admin-uploaded) signatures too: a regeneration
        // replaces the document contract, discarding either kind.
        const fmHasSignatures = ((signableDocumentTypeFM?.signatures?.length ?? 0) > 0)
          || ((signableDocumentTypeFM?.adminSignedFor?.length ?? 0) > 0);

        // Check if FM document is locked by checking if there's a matching locked FM metadata
        const lockedFMMetadata = (lockedFMMetadatas ?? []).find(
          (fm) => fm.signableDocumentId._2 === trade.id,
        )
        const fmIsLocked = !!lockedFMMetadata
        // The locked snapshot is written when the FM is locked, so its
        // createdAt is when the lock happened.
        const fmLockedAt = lockedFMMetadata?.createdAt
          ? formatDocumentTimestamp(lockedFMMetadata.createdAt) || null
          : null

        // Active holds across this sub-trade's signable documents, deduped by
        // lockId — the same lock (e.g. a facility hold cascaded down) commonly
        // appears on more than one document type (C/AA/FM/AL) for the trade.
        const holds: Hold[] = (() => {
          const seen = new Set<string>()
          const result: Hold[] = []
          for (const signableDocument of signableDocuments) {
            for (const h of signableDocument.holds ?? []) {
              if (!h || h.liftedAt || seen.has(h.lockId)) continue
              seen.add(h.lockId)
              result.push(h)
            }
          }
          return result
        })()

        return {
          ...trade,
          id: trade.id ?? '',
          masterTradeId: trade.masterTradeId ?? '',
          lockedDate: trade.lockedDate ?? '',
          status: trade.status?.toLowerCase() ?? '',
          clientId: trade.clientId ?? '',
          systemId: trade.systemId ?? '',
          myEntity: trade.myEntity ?? '',
          agentEntity: agentEntity ?? null,
          cDocumentIcon,
          cDocumentDate: cStatus.timestamp,
          cDocumentStatus: cStatus.status,
          aaDocumentIcon,
          aaDocumentDate: aaStatus.timestamp,
          aaDocumentStatus: aaStatus.status,
          aaDocumentSigned,
          fmDocumentIcon,
          fmDocumentDate: fmStatus.timestamp,
          fmDocumentStatus: fmStatus.status,
          fmLockedAt,
          alDocumentIcon,
          alDocumentDate: alStatus.timestamp,
          alDocumentStatus: alStatus.status,
          fmIsLocked,
          holds,
          isLoadingAAMetadata: trade.isLoadingAAMetadata,
          refreshingDocumentType: refreshingDocument?.subTradeId === trade.id ? refreshingDocument?.documentType : null,
          cHasSignatures,
          aaHasSignatures,
          alHasSignatures,
          fmHasSignatures,
          notRefreshed: notRefreshedBySubTradeId.get(trade.id ?? '') ?? null,
          settlementMethod: row.settlementMethod,
          paymentType: row.paymentType,
          allFacilitiesTerminated:
            trade.tradedFacilities.length > 0 &&
            trade.tradedFacilities.every(
              (tf) => tf.facilityDetails?.status === 'Terminated',
            ),
          deliveryView: deliveryViewFor2(trade.id ?? ''),
        };
      });
  }, [refinedSubTrades, subTrades, allEntities, attachementsMasterTrade, allSignableDocuments, refreshingDocument, lockedFMMetadatas, deliveries, notRefreshedBySubTradeId]);


  const renderDetailPanelAllocations = useCallback(
    (row: { original: RefinedSubTrade; getIsExpanded: () => boolean }) => {
      if (!row.getIsExpanded()) return null;
      return <DocumentTradePanel refinedSubTrade={row.original} />;
    },
    []
  );

  const filteredSubTrades = useMemo(() => {
    return subTradesForDocumentTable
      .filter(trade =>
        statusTable.toLowerCase() === 'all'
          ? true
          : trade.status?.toLowerCase() === statusTable.toLowerCase()
      )
      .sort((a, b) => (a.systemId || '').localeCompare(b.systemId || ''));
  }, [subTradesForDocumentTable, statusTable]);

  const possibleActions = useMemo(() => {
    return getPossibleActions(row, signableDocument);
  }
    , [row, signableDocument]);

  return (
    <>
      <Stack w="100%" className="sectionDetailTradeBody noHeader">
        <Stack w="100%" className="sectionRemoveGap sectionBody">
          <div className="allocation-div" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', maxWidth: '96%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {/* Bulk Action control sits immediately to the left of the Status filter (LOS-5802) */}
              <TradeActions row={row} />
              <NewSelectInput
                wrapperType="trade"
                label="Status"
                data={tradeViewColumns}
                w="164px"
                h="42px"
                placeholder="Status"
                value={statusTable}
                onChange={(value) => setStatusTable(value ?? 'All')}
              />
            </div>
          </div>

          <Stack w="100%" className="sectionDetailTradeBody" style={{ padding: '10px' }}>
            <MainTable
              tableName="AA"
              columnDefs={getColumnDefs(row.myEntity, handleRefreshDocument, (subTradeId, hold) => setLiftTarget({ subTradeId, hold }), openConfirm)}
              withPagination={true}
              initialPagination={pagination}
              onPaginationChange={handlePaginationChange}
              initialColumnVisibility={columnVisibility}
              onColumnVisibilityChange={handleColumnVisibilityChange}
              renderDetailPanel={({ row }) => renderDetailPanelAllocations(row)}
              data={filteredSubTrades ?? []}
              setRow={() => { }}
              enableTopToolbar={false}
              headerBackgroundColor="#F0EEEE"
              disableStriping={true}
              enableColumnOrdering={false}
              enableColumnResizing={false}
              enableGrouping={false}
              enableColumnFilters={false}
              enableColumnActions={false}
              enableFilters={false}
              enableSorting={false}
              enableFullScreenToggle={false}
              enableDensityToggle={false}
              enableHiding={false}
              border={true}
              fromTradeEntry={false}
              isLoading={isLoadingRefinedSubTrades}
              enableRowSelection={true}
              enableMultiRowSelection={true}
              tableSelectedRows={rowSelection}
              setTableSelectedRows={setRowSelection}
            />
          </Stack>
        </Stack>
      </Stack >
      {openPreviewDocument && signableDocument && (
        <DocumentPreviewModal
          opened={openPreviewDocument}
          onClose={() => {
            activeSessionRef.current = null;
            setOpenPreviewDocument(false);
            setCurrentDocumentType('');
            setSignableDocument(null);
            setActiveRefinedSubTrade(null);
          }}
          documentUrl={signableDocument.url ?? ''}
          previewHtml={signableDocument.previewHtml ?? null}
          filename={signableDocument.fileName ?? "Unknown Document.pdf"}
          documentType={currentDocumentType}
          subTrade={activeRefinedSubTrade}
          onAction={handleSignDocument}
          possibleActions={possibleActions}
          hasUnappliedTransactions={hasUnappliedTransactions}
          onRedirectToSubTrade={redirectToSubTrade}
        />
      )}
      <LiftHoldModal
        opened={!!liftTarget}
        close={() => setLiftTarget(null)}
        entityNoun="trade"
        hold={liftTarget?.hold ?? null}
        onSubmit={async (liftReason?: string): Promise<boolean> => {
          if (!liftTarget) return false
          const result: any = await dispatch(
            liftHoldOnSubTrades(
              {
                subTradeIds: [liftTarget.subTradeId],
                ...(liftReason ? { liftReason } : {}),
              },
              () => refreshDocuments(),
            ),
          )
          if (!result?.success) {
            ErrorNotification({ title: 'Lift Lock Failed', message: result?.payload })
            return false
          }
          SuccessNotification({ title: 'Lock Lifted', message: 'The trade lock has been lifted.' })
          return true
        }}
      />
      {refreshWarningModal && (
        <DeleteConfirmation
          title="Warning"
          message="This document has existing signatures. Regenerating will invalidate signatures. Do you want to continue?"
          confirmText="Continue"
          onConfirm={handleConfirmRefresh}
          onCancel={handleCancelRefresh}
        />
      )}
    </>
  );
};

export default DocumentTradeEntryPanel;


